import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faTriangleExclamation } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, PageBanner, Text, toast } from "@performyard/py-components";

import EmployeeSelection from "../../../../../../../components/EmployeeSelection";
import Spacer from "../../../../../../../components/Spacer";
import { SurveyAccessSelect } from "../components/SurveyAccessSelect";

import { useSetters, useTemporarySelectionObjectId } from "../store/store";

import { useGetSurveyRespondents, useUpdateSurvey } from "../../../../../../../api/hooks";
import { useUserSelectionCount } from "../../../../../../../api/hooks/defs/user-selection";
import { useEmployeeSelectionObject } from "../../../../../../../hooks/useEmployeeSelectionObject";
import useUnsavedChanges from "../../../../../../../hooks/useUnsavedChanges";
import { useSurvey } from "../../../hooks/useSurvey";

import { FetchNewSelectionObjectProps, fetchNewSelectionObject } from "../../../../../../../api/services";
import { addSurveyParticipants } from "../../../../../../../api/services/defs/internal-api/surveys";
import {
	addUsersToSelection as addUsersToSelectionAPI,
	getUserSelectionUsers,
} from "../../../../../../../api/services/defs/user-selection";

import pluralizer from "../../../../../../../utils/pluralizer";

import { SURVEY_PARTICIPANTS_CONFIG } from "../constants";

export const SelectParticipants = ({ surveyId }: { surveyId: string }) => {
	const { survey } = useSurvey({ surveyId });
	const setters = useSetters();
	const temporarySelectionObjectId = useTemporarySelectionObjectId();
	const [isCompletingSelection, setIsCompletingSelection] = useState<boolean>(false);
	const [bannerDismissed, setBannerDismissed] = useState<boolean>(false);
	const [isInitializingSelectionObject, setIsInitializingSelectionObject] = useState<boolean>(false);
	const [newlyCreatedSelectionObjectId, setNewlyCreatedSelectionObjectId] = useState<string | null>(null);

	const { setIsSafe, setIsUnsafe } = useUnsavedChanges({ initialState: true });

	const {
		data: alreadyRespondedParticipants,
		isLoading: isLoadingAlreadyRespondedParticipants,
		refetch: refetchAlreadyRespondedParticipants,
	} = useGetSurveyRespondents(surveyId);

	const {
		data: selectionObjectCount,
		isLoading: isLoadingSelectionObjectCount,
		isFetching: isFetchingSelectionObjectCount,
	} = useUserSelectionCount(temporarySelectionObjectId || "");

	const {
		data: alreadySelectedCount,
		isLoading: isLoadingAlreadySelectedCount,
		isFetching: isFetchingAlreadySelectedCount,
	} = useUserSelectionCount(survey?.participantSelection?.selectionObjectId || "");

	const { mutate: updateSurvey, isPending: isUpdatingSurvey } = useUpdateSurvey(surveyId);

	const showBanner: boolean = useMemo(() => {
		if (bannerDismissed) return false;
		if (isLoadingAlreadySelectedCount || isFetchingAlreadySelectedCount) return false;
		if ((alreadySelectedCount?.count || 0) <= 0) return false;
		return true;
	}, [isLoadingAlreadySelectedCount, isFetchingAlreadySelectedCount, alreadySelectedCount, bannerDismissed]);

	const allowCancelSelection: boolean = useMemo(() => {
		if (!survey?.participantSelection?.enabled) return false;
		if (isLoadingAlreadySelectedCount || isFetchingAlreadySelectedCount) return false;
		if ((alreadySelectedCount?.count || 0) <= 0) return false;
		return true;
	}, [
		survey?.participantSelection?.enabled,
		isLoadingAlreadySelectedCount,
		isFetchingAlreadySelectedCount,
		alreadySelectedCount?.count,
	]);

	const {
		addUsersToSelection,
		usersList,
		isAddingUsersToSelection,
		isLoading: isLoadingEmployeeSelectionObject,
	} = useEmployeeSelectionObject({
		selectionObjectID: survey?.participantSelection?.selectionObjectId || "",
		page: SURVEY_PARTICIPANTS_CONFIG.SELECTION_CONFIG.PAGE,
		searchTerm: "",
		exclusionList: [],
		filters: {},
		sortField: SURVEY_PARTICIPANTS_CONFIG.SELECTION_CONFIG.SORT_FIELD,
		sortDirection: SURVEY_PARTICIPANTS_CONFIG.SELECTION_CONFIG.SORT_DIRECTION,
		limit: SURVEY_PARTICIPANTS_CONFIG.SELECTION_CONFIG.LIMIT,
		limitSelected: true,
	});

	useEffect(() => {
		// Set as unsafe initially since we're in edit mode
		setIsUnsafe();
		return () => {
			setIsSafe();
			setters.setTemporarySelectionObjectId(null);
		};
	}, []);

	const alreadySelectedSelectionId = useMemo(() => {
		return survey?.participantSelection?.selectionObjectId;
	}, [survey?.participantSelection?.selectionObjectId]);

	useEffect(() => {
		refetchAlreadyRespondedParticipants();
	}, [refetchAlreadyRespondedParticipants]);

	useEffect(() => {
		// if no selection object exists, create and set the selection object on the survey
		if (!survey?.participantSelection?.selectionObjectId && !newlyCreatedSelectionObjectId) {
			const fetchInitialSelectionObject = async () => {
				setIsInitializingSelectionObject(true);
				const fetchSelectionObjectArgs: FetchNewSelectionObjectProps = {};
				const result = await fetchNewSelectionObject(fetchSelectionObjectArgs);

				setNewlyCreatedSelectionObjectId(result.user_selection_id);

				updateSurvey({
					participantSelection: {
						selection_object_id: result.user_selection_id,
					},
				});
			};
			fetchInitialSelectionObject();
		}
	}, [survey?.participantSelection?.selectionObjectId, newlyCreatedSelectionObjectId, updateSurvey]);

	useEffect(() => {
		// After creating a selection object, wait for already responded participants to load,
		// then add them to the selection object
		const selectionObjectId = newlyCreatedSelectionObjectId || survey?.participantSelection?.selectionObjectId;
		if (selectionObjectId && isInitializingSelectionObject && !isLoadingAlreadyRespondedParticipants) {
			const addInitialUsers = async () => {
				if (alreadyRespondedParticipants?.length && alreadyRespondedParticipants.length > 0) {
					await addUsersToSelectionAPI({
						selectionObjectID: selectionObjectId,
						users: alreadyRespondedParticipants.map((participant) => participant.toString()),
					});
				}
				setIsInitializingSelectionObject(false);
				setNewlyCreatedSelectionObjectId(null);
			};
			addInitialUsers();
		}
	}, [
		newlyCreatedSelectionObjectId,
		survey?.participantSelection?.selectionObjectId,
		isInitializingSelectionObject,
		isLoadingAlreadyRespondedParticipants,
		alreadyRespondedParticipants,
	]);

	useEffect(() => {
		if (!temporarySelectionObjectId) {
			const fetchTemporarySelectionObject = async () => {
				const fetchSelectionObjectArgs: FetchNewSelectionObjectProps = {};
				const result = await fetchNewSelectionObject(fetchSelectionObjectArgs);
				setters.setTemporarySelectionObjectId(result.user_selection_id);
			};
			fetchTemporarySelectionObject();
		}
	}, [temporarySelectionObjectId, setters]);

	useEffect(() => {
		// Update the selection object to include anyone who has already responded to the survey
		// (for existing selection objects that might have new participants)
		// Skip this during initialization to avoid duplicate work
		if (
			!isInitializingSelectionObject &&
			survey?.participantSelection?.selectionObjectId &&
			!isLoadingEmployeeSelectionObject &&
			!isLoadingAlreadyRespondedParticipants &&
			alreadyRespondedParticipants?.length &&
			alreadyRespondedParticipants.length > 0 &&
			alreadySelectedSelectionId
		) {
			const usersToAdd = alreadyRespondedParticipants
				.filter((participant) => !(usersList || []).some((user) => user._id === participant.toString()))
				.map((participant) => participant.toString());

			if (usersToAdd.length > 0) {
				addUsersToSelection({
					selectionObjectID: alreadySelectedSelectionId,
					users: usersToAdd,
				});
			}
		}
	}, [
		isInitializingSelectionObject,
		survey?.participantSelection?.selectionObjectId,
		alreadySelectedSelectionId,
		isLoadingEmployeeSelectionObject,
		isLoadingAlreadyRespondedParticipants,
		alreadyRespondedParticipants,
		usersList,
		addUsersToSelection,
	]);

	const handleCompleteSelection = useCallback(async () => {
		if (!temporarySelectionObjectId || !survey?.participantSelection?.selectionObjectId) return;
		setIsCompletingSelection(true);

		try {
			// Fetch the current selection at the time of completion to avoid race conditions
			const currentSelection = await getUserSelectionUsers({
				selectionObjectID: temporarySelectionObjectId,
				page: SURVEY_PARTICIPANTS_CONFIG.SELECTION_CONFIG.PAGE,
				limitSelected: true,
				limit: selectionObjectCount?.count,
				exclusionList: [],
				filters: {},
				sortField: SURVEY_PARTICIPANTS_CONFIG.SELECTION_CONFIG.SORT_FIELD,
				sortDirection: SURVEY_PARTICIPANTS_CONFIG.SELECTION_CONFIG.SORT_DIRECTION,
			});

			if (!currentSelection?.users || currentSelection.users.length === 0) {
				setIsCompletingSelection(false);
				return;
			}

			// Add participants via internal API (triggers ETL for published surveys)
			await addSurveyParticipants(
				surveyId,
				currentSelection.users.map((user) => user._id)
			);

			// Enable participant selection if not already enabled
			if (!survey?.participantSelection?.enabled) {
				updateSurvey({
					participantSelection: {
						enabled: true,
						selection_object_id: survey?.participantSelection?.selectionObjectId,
					},
				});
			}

			// Use the count from the current selection
			toast.success(
				`${currentSelection.users.length} employee${pluralizer(currentSelection.users.length, " has been added as a participant", "s have been added as participants")}`
			);
			// clear the temporary selection object id
			setters.setTemporarySelectionObjectId(null);
			// set the unsaved changes to safe
			setIsSafe();
			// this will switch the page to the participants page
			setters.setEditParticipants(false);
		} catch (error) {
			toast.error("Failed to add participants");
		} finally {
			setIsCompletingSelection(false);
		}
	}, [
		temporarySelectionObjectId,
		selectionObjectCount?.count,
		survey?.participantSelection?.selectionObjectId,
		survey?.participantSelection?.enabled,
		surveyId,
		updateSurvey,
		setters,
		setIsSafe,
	]);

	const handleCancelSelection = () => {
		setters.setTemporarySelectionObjectId(null);
		setters.setEditParticipants(false);
	};

	return (
		<>
			<SurveyAccessSelect selectionCount={selectionObjectCount?.count || 0} surveyId={surveyId} />
			<Spacer height={2} />
			{showBanner && (
				<>
					<PageBanner
						color="primary"
						size="small"
						mainTitle="Only eligible employees are shown in this table for participant selection."
						icon={faTriangleExclamation}
						onDismiss={() => setBannerDismissed(true)}
					/>
					<Spacer height={2} />
				</>
			)}
			<Text size="paragraph-1" weight="semi-bold">
				Select Participants
			</Text>
			<Spacer height={2} />
			{temporarySelectionObjectId && alreadySelectedSelectionId && !isInitializingSelectionObject && (
				<>
					<EmployeeSelection
						paginationAlignment="end"
						selectionObjectID={temporarySelectionObjectId}
						usersToExcludeSelectionId={alreadySelectedSelectionId}
						customFieldsIdentifiers={["Group", "Manager"]}
						exclusionList={[]}
						filters={{}}
						height={SURVEY_PARTICIPANTS_CONFIG.UI_CONFIG.SELECTION_HEIGHT}
					/>
					<div className="flex justify-end gap-2">
						{allowCancelSelection && (
							<span className="w-fit">
								<ButtonStandard
									color="neutral"
									size="medium"
									variant="outline"
									onClick={handleCancelSelection}
								>
									Cancel
								</ButtonStandard>
							</span>
						)}
						<span className="w-fit">
							<ButtonStandard
								color="green"
								size="medium"
								variant="solid"
								disabled={
									isCompletingSelection ||
									isLoadingSelectionObjectCount ||
									isFetchingSelectionObjectCount ||
									isUpdatingSurvey ||
									isAddingUsersToSelection ||
									selectionObjectCount?.count === 0
								}
								onClick={handleCompleteSelection}
							>
								Complete Selection
							</ButtonStandard>
						</span>
					</div>
				</>
			)}
		</>
	);
};
